import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { useAppDownload } from '@/context/AppDownloadContext';
import { gamificationProfile, gamificationLeaderboard } from '@/mocks/listenerGamification';

export default function Gamificacion() {
  const { openModal: openDownloadModal } = useAppDownload();
  const [activeTab, setActiveTab] = useState<'reglas' | 'actividad' | 'ranking'>('reglas');

  const profile = gamificationProfile;

  const formatTimeAgo = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return new Date(dateStr).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-background-100/50 flex flex-col">
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6 pb-10">

          {/* Hero Gamification Card */}
          <div className="bg-background-50 rounded-2xl overflow-hidden">
            <div className="relative h-44 md:h-56 bg-gradient-to-br from-amber-500/30 via-orange-400/20 to-rose-400/15 overflow-hidden">
              <div className="absolute inset-0 bg-background-50/30"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center relative z-10 px-4 w-full">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <Link to="/niveles" className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-amber-500 flex items-center justify-center hover:bg-amber-600 transition-colors cursor-pointer">
                      <i className="ri-flashlight-line text-background-50 text-2xl"></i>
                    </Link>
                    <Link to="/niveles" className="text-left hover:opacity-80 transition-opacity cursor-pointer">
                      <div className="text-xs text-foreground-500 font-medium">Tu nivel</div>
                      <div className="text-2xl md:text-3xl font-black text-foreground-950">{profile.levelName}</div>
                    </Link>
                  </div>
                  <div className="max-w-md mx-auto">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-foreground-700">Nivel {profile.level}</span>
                      <span className="text-xs font-bold text-foreground-700">{profile.totalPoints.toLocaleString()} pts</span>
                    </div>
                    <div className="w-full h-3 bg-background-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500 rounded-full transition-all duration-1000 ease-out relative"
                        style={{ width: `${profile.progressPercent}%` }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full"></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[11px] text-foreground-500">Nivel {profile.level}</span>
                      <span className="text-[11px] text-foreground-500">
                        {profile.pointsToNextLevel - profile.totalPoints} pts para {profile.nextLevel}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="px-6 md:px-8 pb-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 -mt-8 relative z-10">
                {[
                  { label: 'Esta Semana', value: `${profile.stats.weeklyPoints}`, sub: `/ ${profile.stats.weeklyGoal} meta`, icon: 'ri-calendar-line', color: 'bg-primary-500' },
                  { label: 'Hoy', value: `${profile.stats.todayPoints}`, sub: 'puntos', icon: 'ri-sun-line', color: 'bg-accent-500' },
                  { label: 'Racha', value: `${profile.stats.streakDays}`, sub: 'días seguidos', icon: 'ri-fire-line', color: 'bg-orange-500' },
                  { label: 'Top Acción', value: profile.stats.topCategory, sub: profile.stats.topStation, icon: 'ri-trophy-line', color: 'bg-amber-500' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-background-50 rounded-xl p-3 shadow-sm">
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${stat.color}`}>
                        <i className={`${stat.icon} text-background-50 text-xs`}></i>
                      </div>
                      <span className="text-[10px] text-foreground-500 font-medium">{stat.label}</span>
                    </div>
                    <div className="text-lg font-bold text-foreground-950">{stat.value}</div>
                    <div className="text-[10px] text-foreground-400 truncate">{stat.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex items-center gap-1 bg-background-100 rounded-full p-1 max-w-sm mx-auto">
            {([
              { key: 'reglas', label: 'Reglas de Puntos', icon: 'ri-information-line' },
              { key: 'actividad', label: 'Actividad', icon: 'ri-history-line' },
              { key: 'ranking', label: 'Ranking', icon: 'ri-medal-line' },
            ] as const).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-background-50 text-foreground-950 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className={`${tab.icon} text-xs`}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {/* Reglas de Puntos */}
            {activeTab === 'reglas' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {profile.rules.map((rule) => (
                    <div key={rule.id} className="bg-background-50 rounded-2xl p-5 hover:bg-background-50 transition-colors">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${rule.color}`}>
                          <i className={`${rule.icon} text-background-50 text-xl`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-bold text-foreground-950 mb-1">{rule.action}</h4>
                          <p className="text-xs text-foreground-500 leading-relaxed mb-3">{rule.description}</p>
                          <div className="flex items-center gap-2">
                            <span className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg text-xs font-bold">
                              +{rule.points} pts
                            </span>
                            <span className="text-xs text-foreground-400">{rule.unit}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total Stats by Action */}
                <div className="mt-6 bg-background-50 rounded-2xl p-6">
                  <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                    <i className="ri-bar-chart-line text-accent-500"></i>
                    Tu Desglose de Puntos
                  </h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Escuchar radio', count: profile.stats.radioHours, unit: 'horas', points: profile.stats.radioHours * 20, icon: 'ri-headphone-line', color: 'bg-primary-500', barColor: 'bg-primary-500' },
                      { label: 'Enviar saludo', count: profile.stats.greetingsSent, unit: 'saludos', points: profile.stats.greetingsSent * 25, icon: 'ri-message-2-line', color: 'bg-accent-500', barColor: 'bg-accent-500' },
                      { label: 'Solicitar canción', count: profile.stats.songsRequested, unit: 'canciones', points: profile.stats.songsRequested * 30, icon: 'ri-music-line', color: 'bg-emerald-500', barColor: 'bg-emerald-500' },
                      { label: 'Participar concurso', count: profile.stats.contestsJoined, unit: 'concursos', points: profile.stats.contestsJoined * 100, icon: 'ri-trophy-line', color: 'bg-amber-500', barColor: 'bg-amber-500' },
                      { label: 'Asistir evento', count: profile.stats.eventsAttended, unit: 'eventos', points: profile.stats.eventsAttended * 75, icon: 'ri-calendar-check-line', color: 'bg-rose-500', barColor: 'bg-rose-500' },
                      { label: 'Compartir contenido', count: profile.stats.contentShared, unit: 'contenidos', points: profile.stats.contentShared * 15, icon: 'ri-share-forward-line', color: 'bg-sky-500', barColor: 'bg-sky-500' },
                    ].map((item) => {
                      const maxPoints = Math.max(...[470 * 2, 8 * 25, 5 * 30, 3 * 100, 2 * 75, 7 * 15]) * 1.2;
                      const barWidth = Math.min((item.points / maxPoints) * 100, 100);

                      return (
                        <div key={item.label} className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${item.color}`}>
                            <i className={`${item.icon} text-background-50 text-sm`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-foreground-800">{item.label}</span>
                              <span className="text-sm font-bold text-foreground-950">{item.points} pts</span>
                            </div>
                            <div className="w-full h-2 bg-background-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-700 ease-out ${item.barColor}`}
                                style={{ width: `${barWidth}%` }}
                              ></div>
                            </div>
                            <div className="text-[10px] text-foreground-400 mt-0.5">{item.count} {item.unit}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Actividad */}
            {activeTab === 'actividad' && (
              <div className="bg-background-50 rounded-2xl overflow-hidden">
                <div className="px-6 py-4 border-b border-background-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                      <i className="ri-history-line text-foreground-600"></i>
                      Historial de Puntos
                    </h3>
                    <span className="text-xs text-foreground-500">{profile.history.length} registros</span>
                  </div>
                </div>
                <div className="divide-y divide-background-100">
                  {profile.history.map((entry) => (
                    <div key={entry.id} className="px-6 py-3.5 flex items-center gap-4 hover:bg-background-50 transition-colors">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${
                        entry.action === 'Escuchar radio' ? 'bg-primary-500' :
                        entry.action === 'Enviar saludo' ? 'bg-accent-500' :
                        entry.action === 'Solicitar canción' ? 'bg-emerald-500' :
                        entry.action === 'Participar concurso' ? 'bg-amber-500' :
                        entry.action === 'Asistir evento' ? 'bg-rose-500' : 'bg-sky-500'
                      }`}></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground-800">{entry.action}</div>
                        <div className="text-xs text-foreground-500">{entry.station}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-bold text-foreground-950">+{entry.points}</div>
                        <div className="text-xs text-foreground-400">{formatTimeAgo(entry.timestamp)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ranking */}
            {activeTab === 'ranking' && (
              <div className="bg-background-50 rounded-2xl overflow-hidden">
                <div className="px-6 py-4 border-b border-background-200">
                  <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                    <i className="ri-medal-line text-amber-500"></i>
                    Top Oyentes
                  </h3>
                </div>
                <div className="divide-y divide-background-100">
                  {gamificationLeaderboard.map((entry, idx) => (
                    <div
                      key={entry.rank}
                      className={`px-6 py-3.5 flex items-center gap-4 transition-colors ${
                        entry.username === '@marco_oyente' ? 'bg-amber-50' : 'hover:bg-background-50'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                        entry.rank === 1 ? 'bg-amber-500 text-background-50' :
                        entry.rank === 2 ? 'bg-foreground-300 text-background-50' :
                        entry.rank === 3 ? 'bg-orange-400 text-background-50' :
                        'bg-background-100 text-foreground-600'
                      }`}>
                        {entry.rank}
                      </div>
                      <img
                        src={entry.photo}
                        alt={entry.name}
                        className="w-9 h-9 rounded-xl object-cover object-top shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-foreground-950 flex items-center gap-1.5">
                          {entry.name}
                          {entry.username === '@marco_oyente' && (
                            <span className="text-[10px] text-amber-600 font-medium">(Vos)</span>
                          )}
                        </div>
                        <div className="text-xs text-foreground-500">{entry.username} · Nivel {entry.level}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-bold text-foreground-950">{entry.points.toLocaleString()}</div>
                        <div className="text-[10px] text-foreground-400">pts</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Milestones */}
          <div className="mt-6 bg-background-50 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-flag-line text-accent-500"></i>
                Hitos de Oyente
              </h3>
              <span className="text-xs text-foreground-500">{profile.milestones.filter((m) => m.reached).length}/{profile.milestones.length} alcanzados</span>
            </div>
            <div className="relative">
              {/* Timeline track */}
              <div className="absolute left-[15px] top-3 bottom-3 w-0.5 bg-background-200"></div>

              <div className="space-y-4">
                {profile.milestones.map((milestone) => (
                  <div key={milestone.name} className="flex items-center gap-4 relative">
                    {/* Milestone dot */}
                    <div className={`relative z-10 w-[31px] h-[31px] rounded-full flex items-center justify-center shrink-0 border-2 ${
                      milestone.reached
                        ? `${milestone.color} border-background-50`
                        : 'bg-background-200 border-background-100'
                    }`}>
                      {milestone.reached ? (
                        <i className={`${milestone.icon} ${milestone.points <= 3000 ? 'text-background-50' : 'text-background-50'} text-xs`}></i>
                      ) : (
                        <div className="w-2.5 h-2.5 rounded-full bg-background-300"></div>
                      )}
                    </div>
                    <div className={`flex-1 bg-background-100 rounded-xl p-3 ${!milestone.reached ? 'opacity-50' : ''}`}>
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-foreground-950">{milestone.name}</div>
                        <div className={`text-xs font-bold ${milestone.reached ? 'text-accent-600' : 'text-foreground-400'}`}>
                          {milestone.points.toLocaleString()} pts
                        </div>
                      </div>
                      {milestone.reached && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <i className="ri-checkbox-circle-fill text-accent-500 text-xs"></i>
                          <span className="text-xs text-accent-600 font-medium">Alcanzado</span>
                        </div>
                      )}
                      {!milestone.reached && milestone.name === 'Oyente Maestro' && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <div className="flex-1 h-1.5 bg-background-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-accent-500 rounded-full"
                              style={{ width: `${profile.progressPercent}%` }}
                            ></div>
                          </div>
                          <span className="text-[10px] text-foreground-500">{profile.progressPercent}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-10 bg-background-50 rounded-2xl p-8 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-amber-500 flex items-center justify-center">
              <i className="ri-rocket-line text-background-50 text-2xl"></i>
            </div>
            <h3 className="text-lg font-bold text-foreground-950 mb-2">Llevá tu nivel al máximo</h3>
            <p className="text-sm text-foreground-500 mb-5 max-w-md mx-auto">
              Cada acción suma. Escuchá, participá, compartí. Cuanto más interactuás, más subís en el ranking de oyentes RFW.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={openDownloadModal}
                className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line mr-1.5"></i>
                Descargar App RFW
              </button>
              <Link
                to="/insignias"
                className="px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-award-line mr-1.5"></i>
                Ver insignias
              </Link>
              <Link
                to="/club-vip"
                className="px-5 py-2.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-vip-crown-line mr-1.5"></i>
                Club VIP
              </Link>
              <Link
                to="/top-fans"
                className="px-5 py-2.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-star-line mr-1.5"></i>
                Top Fans
              </Link>
            </div>
          </div>

        </div>
      </main>

      <Footer />

      <div className="md:hidden h-20"></div>
    </div>
  );
}